<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_REGION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="otherBusinessRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="regionList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_REGION')"
              @click="onSelectRegionItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectRegionItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="regionRules">
        <b-form>
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="6">
              <InputField
                id="name"
                v-model="regionFormData.name"
                :label="$t('Name')"
                :placeholder="$t('Enter name')"
                type="text"
                :error-name="$t('Name')"
                rules="required"
                for="name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Name -->

            <!-- Code -->
            <b-col md="6">
              <InputField
                id="code"
                v-model="regionFormData.code"
                :label="$t('Code')"
                :placeholder="$t('Enter code')"
                type="number"
                :error-name="$t('Code')"
                rules="required|length:3|integer"
                for="code"
                :readonly="isEditable"
              />
            </b-col>

          </b-row>

          <b-row class="match-height">

            <!-- Description -->
            <b-col md="6">
              <InputField
                id="description"
                v-model="regionFormData.description"
                :label="$t('Description')"
                :placeholder="$t('Enter description')"
                type="text"
                :error-name="$t('Description')"
                rules="required"
                for="description"
                :readonly="isReadonly"
              />
            </b-col>
            <!--Description -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchRegions"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

  </b-card>

</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BDropdownItem,
  BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import Button from '../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    SelectField,
    BTable,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    BDropdownItem,
    BDropdown,
    TablePagination,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      query: '',
      isReadonly: false,
      isEditable: false,
      sortBy: 'name',
      buttonLabel: this.$t('Save'),
      required,
      modalTitle: this.$t('Create Region'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'description', sortable: true, label: 'Description' },
        { key: 'actions', label: 'Actions' },
      ],
      regionFormData: {
        name: '',
        code: '',
        description: '',
        id: '',
      },
      filter: {
        name: '',
        code: '',
        status: '',
        sortColumn: '',
      },
      regionList: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchRegions()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchRegions()
    },
  },
  mounted() {
    this.fetchRegions()
  },

  methods: {
    ...mapActions({
      getRegion: 'operations/getRegionList',
      updateRegion: 'operations/updateRegionAction',
      createRegion: 'operations/createRegionAction',
    }),
    async onSelectRegionItem(items, activity, val) {
      this.isEditable = true
      this.regionFormData.name = items.name
      this.regionFormData.code = items.code
      this.regionFormData.description = items.description
      this.regionFormData.id = items.id
      this.isEditable = true
      this.isReadonly = val === 'edit' ? (this.isReadonly = false) : (this.isReadonly = true)
      this.modalTitle = val === 'edit' ? this.$t('Edit Region') : this.$t('Region')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async regionAction() {
      const yourRequest = this.$t('Your request to')
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createRegion(this.regionFormData)
          : await this.updateRegion(this.regionFormData)
        const { status } = await response
        if (status === 200) {
          this.fetchRegions()
          this.hideModal('modal-lg')
          const successText = this.$t('Region was successful')
          this.alertSuccess({
            text: `${yourRequest} ${this.buttonLabel} ${successText}`,
          })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        const reasonText = this.$t('Region failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${yourRequest} ${this.buttonLabel} ${reasonText} ${responseError.errorMessage}` })
        })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    async handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.regionRules.validate().then(async success => {
          if (success) {
            const areYouSure = this.$t('Are you sure you want to')
            const thisRegion = this.$t('this Region')
            this.confirm(this.regionAction, {
              text: `${areYouSure} ${this.buttonLabel} ${thisRegion}?`,
            })
          }
        })
      } catch (error) {
        this.hideModal('modal-lg')
        this.alertError({ text: error })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    async fetchRegions() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getRegion({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.regionList = data.data
          this.totalCount = data.totalItems
        } else {
          this.alertError()
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    resetModal() {
      this.regionFormData = { ...null }
      this.isEditable = false
      this.isLoading = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create Region')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchRegions()
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
